<script setup>
  import {computed, inject, nextTick, onBeforeUnmount, ref, useId, useSlots, watch} from "vue";

  import "./Utils/Editor.js";
  import ComponentPrivacyGuard from "@components/Alerts/PrivacyGuard.vue";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentEditor",
  });

  const props = defineProps({
    modelValue: {
      type: String,
      default: "",
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: [String, Boolean],
      default: false,
    },
    hideToolbar: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    enableResize: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: "",
    },
    validation: {
      type: String,
      default: null,
    },
    privacyGuard: {
      type: [String, null],
      default: undefined, // undefined, when attribute is not set at all
    },
    helperText: {
      type: String,
      default: null,
    },
    editorClass: {
      type: String,
      default: null,
    },
    encrypted: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["initialized", "update:modelValue", "click", "mousedown", "blur", "decrypted"]);

  const privacy = inject("$privacy");
  const slots = useSlots();

  const lastValue = ref("");
  const charCount = ref(0);
  const slotObserver = ref(null);

  const refTrix = ref(null);
  const slotContent = ref(null);
  const labelId = useId();

  const maxlength = computed(() => props.maxlength ?? 50_000);

  watch(
    () => props.modelValue,
    (newValue) => {
      if (refTrix.value && newValue !== refTrix.value.value) {
        if (props.encrypted) {
          privacy.decryptValue(newValue).then((res) => {
            if (res !== refTrix.value.value) {
              loadHtml(newValue);
            }
          });
        } else {
          loadHtml(newValue);
        }
      }
    },
  );

  onBeforeUnmount(() => {
    slotObserver?.value?.disconnect();
  });

  const loadHtml = (html, between = null) => {
    const loader = (htmlContent) => {
      nextTick(() => {
        // does refTrix still exists?
        const element = refTrix.value;
        if (!element || !element.editor) return;

        if (element.value) {
          // if the editor already has content, insert the new content after creating an undo entry
          element.editor.recordUndoEntry();
          element.editor.composition.replaceHTML(htmlContent ?? "");
        } else {
          element.editor.loadHTML(htmlContent ?? ""); // avoid internal trix error when loading a null value
        }
        if (typeof between === "function") between();
        lastValue.value = element.value;
      });
    };

    if (props.encrypted && html) {
      privacy.decryptValue(html).then((res) => {
        // check if user has already navigated away
        if (!refTrix.value) return;

        // update lastValue after decryption
        // to properly detect changes (dirty state)
        lastValue.value = res;
        loader(res);
        emit("decrypted", res);
      });
    } else {
      loader(html);
    }
  };

  const initEventHandler = () => {
    if (!refTrix.value) return;

    if (!props.disabled && props.autofocus) {
      focus();
    }

    if (props.modelValue && props.modelValue !== "") {
      loadHtml(props.modelValue);
    } else if (slotContent.value?.innerHTML?.length > 0) {
      loadHtml(slotContent.value.innerHTML, () => {
        refTrix.value.value = refTrix.value.value.replaceAll("<p>&nbsp;", "<p>").replaceAll("<br>&nbsp;", "<br>");
      });
    }

    emit("initialized", lastValue.value);

    // The observer exists solely to keep the editor in sync with *our own* slot content
    // (reactive interpolations, currently only used by RecordSendMailDialog). Without a
    // slot it must not be armed at all: the container is then permanently empty and any
    // foreign DOM mutation on it - e.g. a browser extension marking freshly inserted
    // nodes after the editor got mounted - would run loadHTML("") and silently wipe the
    // editor content (observed in the claim editors of ClaimTextBox).
    if (!slots.default) return;

    slotObserver.value = new MutationObserver(() => {
      const slotHtml = slotContent.value?.innerHTML ?? "";

      // an empty slot means the mutation did not originate from our own slot content,
      // so it must never be used to clear the editor
      if (!refTrix.value?.editor || slotHtml.trim().length === 0) return;

      refTrix.value.editor.loadHTML(slotHtml);
      refTrix.value.value = refTrix.value.value.replaceAll("<p>&nbsp;", "<p>").replaceAll("<br>&nbsp;", "<br>");
      lastValue.value = refTrix.value.value;
    });

    slotObserver.value.observe(slotContent.value, {
      // attributes are intentionally not observed: attribute changes on the container
      // (classes/markers injected by browser extensions) never change the slot content
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  const trixChangeEventHandler = () => {
    // do not emit an updated value if nothing has changed
    if (refTrix.value.value === lastValue.value) return;

    lastValue.value = refTrix.value.value;

    if (props.encrypted) {
      privacy.encryptValue(refTrix.value.value).then((res) => {
        emit("update:modelValue", res);
      });
    } else {
      emit("update:modelValue", refTrix.value.value);
    }

    charCount.value = refTrix.value.editor.getDocument().getLength() - 1;
  };

  const blurEventHandler = () => emit("blur");

  const pasteEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const clipboardData = event.clipboardData || globalThis.clipboardData;
    const pastedData = clipboardData.getData("Text");

    const selectedLength = refTrix.value.editor.getSelectedDocument().getLength() - 1;

    if (pastedData && pastedData !== "" && charCount.value + pastedData.length - selectedLength <= maxlength.value) {
      refTrix.value.editor.insertString(pastedData);
    }
  };

  const fileAcceptEventHandler = (event) => event.preventDefault();

  const mousedownEventHandler = (event) => emit("mousedown", event);

  const keypressEventHandler = (event) => {
    if (charCount.value >= maxlength.value) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const focus = () => {
    if (refTrix.value && !props.disabled) refTrix.value.focus();
  };

  const blur = () => {
    if (refTrix.value && !props.disabled) refTrix.value.blur();
  };

  defineExpose({
    focus,
    blur,
  });
</script>

<template>
  <div :class="{'enable-resize': enableResize}">
    <span
      v-if="label.length > 0"
      :id="labelId"
      class="origin-0 -z-1 cursor-default truncate text-sm duration-300"
      :class="{'text-mcred': validation || privacyGuard, 'text-gray-500': !validation}"
      :data-test="getTestId('trix-editor-label')"
    >
      {{ label }}
    </span>

    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- delegation container: the click is only forwarded for anchors rendered inside the editor content; the operable control is the contenteditable trix-editor -->
    <div :class="cn('py-1', {disabled: disabled, 'hide-toolbar': hideToolbar})" @click="$emit('click', $event)">
      <trix-editor
        ref="refTrix"
        :aria-labelledby="label.length > 0 ? labelId : undefined"
        class="overflow-x-hidden overflow-y-auto text-left text-clip"
        :class="editorClass"
        :contenteditable="!disabled"
        :placeholder="placeholder"
        :data-test="getTestId('trix-editor')"
        @trix-initialize="initEventHandler"
        @trix-change="trixChangeEventHandler"
        @paste="pasteEventHandler"
        @trix-blur="blurEventHandler"
        @trix-file-accept="fileAcceptEventHandler"
        @mousedown="mousedownEventHandler"
        @keypress="keypressEventHandler"
      />

      <div class="mt-1 flex cursor-default justify-between space-x-2">
        <div class="text-xs">
          <div
            v-if="typeof privacyGuard === 'undefined' || validation"
            class="text-mcred"
            :data-test="getTestId('trix-editor-error-message')"
          >
            <slot name="validation">{{ validation }}</slot>
          </div>

          <div v-if="typeof privacyGuard !== 'undefined'" :data-test="getTestId('trix-editor-privacy-guard')">
            <slot v-if="$slots['privacy-guard']" name="privacy-guard" />
            <ComponentPrivacyGuard v-else :validation="privacyGuard" />
          </div>

          <div
            v-if="($slots['helper-text'] || helperText) && !disabled"
            class="text-gray-500"
            :data-test="getTestId('trix-editor-helper-text')"
          >
            <slot name="helper-text" />
            {{ helperText }}
          </div>
        </div>

        <div
          v-if="maxlength && !disabled"
          class="text-right text-xs text-gray-500"
          :class="{'text-mcred': charCount >= maxlength}"
          :data-test="getTestId('trix-editor-max-length')"
        >
          {{ charCount }}/{{ maxlength }}
        </div>
      </div>
    </div>

    <div ref="slotContent" class="hidden">
      <slot />
    </div>
  </div>
</template>

<style scoped>
  /* see resources/css/editor.css */
</style>
